import {useCOTracking} from '@aboutyou/tracking';
import {useEffect, useRef} from 'react';
import {useCheckoutBasketContainsHypedItems} from '../scayle-state/useCheckoutBasketContainsHypedItems';
import {useScaylePaymentMethodState} from '../scayle-state/useScaylePaymentMethodState';
import type {
  CheckoutPaymentTranslationKey,
  ScaylePaymentOption,
} from '../scayle-state/useScayleState';
import {dispatchCheckoutTrackingEvent} from './CheckoutTrackingListener';

/**
 * Dispatches a `bnpl_status` tracking event on first observation of
 * `bnplOption` and whenever `bnplOption.isDisabled` changes afterwards.
 * No-op while `bnplOption` is undefined.
 */
export const useBnplStatusTracking = () => {
  const {bnplOption} = useScaylePaymentMethodState();
  const containsHypedItems = useCheckoutBasketContainsHypedItems();

  const lastEmittedDisabledRef = useRef<boolean | null>(null);

  const loggedRiskCheck1Result = useRef(false);
  const {track} = useCOTracking();

  useEffect(() => {
    if (!bnplOption) {
      return;
    }

    if (lastEmittedDisabledRef.current === bnplOption.isDisabled) {
      return;
    }

    lastEmittedDisabledRef.current = bnplOption.isDisabled;
    const bnplStatusEvent = buildEvent(bnplOption, containsHypedItems);
    dispatchCheckoutTrackingEvent(bnplStatusEvent);

    if (!loggedRiskCheck1Result.current) {
      loggedRiskCheck1Result.current = true;
      track({
        type: 'RiskCheck1Event',
        is_bnpl_enabled: !bnplOption.isDisabled,
        notifications: bnplOption.notifications
          ?.map(n => n.textTranslationKey)
          .join('|'),
      });
    }
  }, [bnplOption, containsHypedItems, track]);
};

const buildEvent = (
  bnplOption: ScaylePaymentOption,
  containsHypedItems: boolean,
) => {
  const notificationKeys = new Set(
    bnplOption.notifications?.map(n => n.textTranslationKey),
  );
  const has = (key: CheckoutPaymentTranslationKey) => notificationKeys.has(key);

  const risk_check_1_result = has(
    'payment.notifications.initialRiskAssessmentDenied',
  );

  const hasRC2Failure = has('payment.notifications.riskAssessmentDenied');

  return {
    event: 'bnpl_status' as const,
    context: {
      status: !bnplOption.isDisabled,

      // risk_check_1_result property is always required
      risk_check_1_result,

      // include risk_check_2_result property only if RC2 failure happened
      ...(hasRC2Failure ? {risk_check_2_result: hasRC2Failure} : {}),

      age: has('payment.notifications.constraintMinAge'),
      min_ov: has('payment.notifications.constraintMinOrderValue'),
      max_ov: has('payment.notifications.constraintMaxOrderValue'),
      blocked_for_invoice: has('payment.notifications.blockedForInvoice'),
      technical_error: has('payment.notifications.errorTechnical'),
      hyped_article: containsHypedItems,
      reasons:
        bnplOption.notifications
          ?.map(n => n.textTranslationKey.replace('payment.notifications.', ''))
          .join('|') ?? null,
    },
  };
};
